/**
 * Where a catalog model that goes on a wall lands: a door, a window or an arch into the wall, a picture, a mirror or a
 * wall cabinet onto its face, a sideboard against it at floor level. The app's rules (`ProjectUIHelper.AddFurniture`,
 * `ProjectHelper.AddFurniture`): a model with an opening tag is an opening of the wall it is put at; anything else
 * takes the wall's face, the opening at the window's sill or on the floor, the rest centred on the wall's height above
 * its plinth (`ValidateWallFurniturePosition`), with no check of its height. A tap takes a wall side of the room
 * (`GetWallNearScreenCenter`): the face under the middle of the view when a room faces it, else the room side nearest
 * the point aimed at, on that face or on the floor. A drop hangs a piece on the face under the pointer, whichever side
 * it is; a piece that stands on the floor takes a room side as a tap does.
 */
import type { Item, Level, RGB, Surface, Vec2, Wall } from '../core/hp/types';
import { contains, projectPoint } from '../core/geometry/edit';
import { roomsBesideWall } from '../core/geometry/roomFloor';
import { usesInches } from '../core/hp/defaults';
import { landingElevation } from '../core/geometry/landing';
import { t } from '../i18n/I18n';
import { furnitureType, isWallFurniture, type CatalogItem } from './catalog';
import type { CardDefinition } from './catalogCard';
import { OPENING_SPECS, type CatalogOpening, type OpeningKind } from './planConstruction';

export type WallFace = 'front' | 'back';
/** A place on a wall's face: the wall, how far along it from its start, and the face. */
export interface WallSpot { wall: Wall; along: number; side: WallFace }

/**
 * The app's `ConfigStorage.OpeningFurnitureTags` (Config.xml «OpeningFurniture»), by the kind of opening the web makes of
 * them. A door first: the app counts a window that is also a door as a door (`FurnitureInfo.IsWindow`). `Niche` stays a
 * wall piece, as the web draws a niche as a region box (`src/core/hp/regions.ts`), never as a hole.
 */
const OPENING_TAGS: ReadonlyArray<readonly [OpeningKind, readonly string[]]> = [
  ['door', ['Door', 'Entrance', 'GarageDoor', 'WallDoor']],
  ['window', ['Window']],
  ['arch', ['Arch', 'Gap']],
];
export function catalogOpeningKind(tags: readonly string[]): OpeningKind | null {
  return OPENING_TAGS.find(([, names]) => tags.some(tag => names.includes(tag)))?.[0] ?? null;
}

/**
 * The kind of opening a catalog card makes, or null for a piece that is not one: by its tags, and an opening the app
 * builds by code (`builtByCode`) is a bare hole whatever its tag — the web's hole without a model is an arch.
 */
export function catalogOpeningKindOf(entry: Pick<CatalogItem, 'tags' | 'builtByCode'>): OpeningKind | null {
  return catalogOpeningKind(entry.tags) ?? (entry.builtByCode === 'opening' ? 'arch' : null);
}

/**
 * The app opens a bare hole 80 cm up (`ValidateWallFurniturePosition`, `ModelSettings.DefaultWindowBottomOffset`): `Gap`
 * is no floor piece in its Config.xml, unlike a door or an arch.
 */
const BARE_OPENING_SILL = .8;

/** The opening a catalog door, window or arch makes: its catalog width and height, a window at the editor's sill. */
export function catalogOpening(entry: CatalogItem, kind: OpeningKind): CatalogOpening {
  const sill = kind === 'window' ? OPENING_SPECS.window.sill : entry.builtByCode === 'opening' ? BARE_OPENING_SILL : 0;
  return { modelRef: { ...entry.modelRef }, caption: entry.title, width: entry.size[0], height: entry.size[1], sill };
}

/** Whether the wall can take something this wide, and this tall when a height is given (an opening). */
export const wallTakes = (wall: Wall, width: number, height?: number) => !wall.hidden && width <= wall.length - .04 && (height === undefined || height <= wall.height);

/**
 * The wall nearest a floor point among those that `takes`, with the place on it facing the point; with `roomSide`, only
 * a wall with a room in front of it, on that room's face. Of two copies of a shared wall, the one whose own room the
 * point is in: its front faces the point.
 */
export function nearestWallSpot(level: Pick<Level, 'walls' | 'rooms'>, point: Vec2, takes: (wall: Wall) => boolean, roomSide = false): WallSpot | null {
  let best: (WallSpot & { distance: number }) | null = null;
  for (const wall of level.walls) {
    if (!takes(wall)) continue;
    const hit = projectPoint(point, wall.start, wall.end);
    const toward = (point.x - hit.point.x) * wall.normalIn.x + (point.z - hit.point.z) * wall.normalIn.z;
    let side: WallFace = toward >= 0 ? 'front' : 'back';
    if (roomSide) {
      const beside = roomsBesideWall(level, wall);
      if (!beside.front && !beside.back) continue;
      if (!beside[side]) side = side === 'front' ? 'back' : 'front';
    }
    const better = !best || hit.distance < best.distance - 1e-6 || (Math.abs(hit.distance - best.distance) <= 1e-6 && side === 'front' && best.side === 'back');
    if (better) best = { wall, along: hit.t * wall.length, side, distance: hit.distance };
  }
  return best && { wall: best.wall, along: best.along, side: best.side };
}

/**
 * How high a wall piece hangs when nothing says: centred on the wall's height above its plinth, as the app centres a
 * picture (`ValidateWallFurniturePosition`); the bottom, which `elevation` measures for a wall piece.
 */
export function defaultWallElevation(wall: Wall, side: WallFace, height: number): number {
  const plinth = side === 'front' ? wall.plinth?.height ?? 0 : 0;
  return wall.height / 2 + plinth - height / 2;
}

/**
 * Where a wall piece of this size sits at `along` on the face `side`, its bottom at `elevation`: clear of the wall's
 * ends, below its top, facing out of the face — the arithmetic of the 3D move along a wall.
 */
export function wallItemPlacement(wall: Wall, size: Item['size'], along: number, side: WallFace, elevation: number): Pick<Item, 'position' | 'elevation' | 'rotation' | 'wallId'> {
  const n = side === 'back' ? { x: -wall.normalIn.x, z: -wall.normalIn.z } : wall.normalIn;
  const at = Math.max(size.w / 2, Math.min(wall.length - size.w / 2, along));
  const reach = wall.thickness / 2 + size.d / 2 + .005;
  return {
    position: { x: wall.start.x + wall.dir.x * at + n.x * reach, z: wall.start.z + wall.dir.z * at + n.z * reach },
    elevation: Math.max(0, Math.min(wall.height - size.h, elevation)),
    rotation: Math.atan2(-n.x, n.z),
    wallId: wall.id,
  };
}

/**
 * Where a piece goes once its model is replaced (`ProjectUIHelper.ReplaceFurniture`): null, its place kept, for a model
 * of the same app type (`GetFurnitureType`); else the new model's own surface, as the app re-adds it — on the floor at
 * the foot of the old wall or under the old lamp, on the old wall or the nearest wall a room faces, or under the ceiling.
 */
export function replacementPlace(level: Level, before: Item, replacement: Pick<CatalogItem, 'tags' | 'placement'>, after: Item): ReplacementPlace | null {
  return replaced(level, before, replacement, after)?.place ?? null;
}

type ReplacementPlace = Pick<Item, 'placement' | 'position' | 'elevation' | 'rotation' | 'wallId'>;
type Against = { wallId: string; side: WallFace };

/** `replacementPlace` and the wall face it went against; `against` keeps that face. */
function replaced(level: Level, before: Item, replacement: Pick<CatalogItem, 'tags' | 'placement'>, after: Item, against?: Against): { place: ReplacementPlace; against?: Against } | null {
  const typeOf = (piece: { tags?: readonly string[]; placement: Item['placement'] }) => piece.tags?.length ? furnitureType(piece.tags) : piece.placement;
  if (typeOf(replacement) === typeOf(before)) return null;
  const { size, position, rotation } = after;
  if (replacement.placement === 'ceiling') return { place: { placement: 'ceiling', position, rotation, elevation: Math.max(level.height - size.h, 0), wallId: undefined } };
  const stands = replacement.placement === 'floor' && isWallFurniture(replacement.tags);
  const kept = against && nearestWallSpot(level, position, wall => wall.id === against.wallId);
  const hung = kept ? { ...kept, side: against!.side } : before.placement === 'wall' && before.wallId ? nearestWallSpot(level, position, wall => wall.id === before.wallId) : null;
  const spot = hung ?? (replacement.placement === 'wall' || stands ? nearestWallSpot(level, position, wall => wallTakes(wall, size.w), true) : null);
  const face = spot ? { wallId: spot.wall.id, side: spot.side } : undefined;
  if (replacement.placement === 'wall') {
    if (!spot) throw new Error(t('#EditorWallItemNoWall'));
    return { place: { placement: 'wall', ...wallItemPlacement(spot.wall, size, spot.along, spot.side, defaultWallElevation(spot.wall, spot.side, size.h)) }, against: face };
  }
  const floor: Item = { ...after, placement: 'floor', elevation: 0, ...(spot ? wallItemPlacement(spot.wall, size, spot.along, spot.side, 0) : {}) };
  return { place: { placement: 'floor', position: floor.position, rotation: floor.rotation, elevation: landingElevation(level, floor, floor.position).elevation, wallId: undefined }, against: face };
}

/** A replacement's place as the properties sheet shows it: the size it is for, and the wall face it stands against. */
export type ShownPlace = ReplacementPlace & Pick<Item, 'size'> & { against?: Against };

/**
 * The place the properties sheet shows for a replacement of another type at `size`, on the face `against` when given;
 * null when it keeps the piece's own place or finds no wall.
 */
export function shownReplacement(level: Level, item: Item, entry: CatalogItem, size: Item['size'], against?: Against): ShownPlace | null {
  try {
    const shown = replaced(level, item, entry, { ...item, size }, against);
    return shown && { ...shown.place, size, ...(shown.against ? { against: shown.against } : {}) };
  } catch {
    return null;
  }
}

/**
 * One cell of the app's plan grid in metres (1 / ModelSettings.GridCellSizeInMeters): 3 ft where the app measures in
 * inches, a tatami's 91 cm for Japan (a `ja` culture), else a metre.
 */
export function gridCellMeters(culture: string | undefined): number {
  if (usesInches(culture)) return 3 * .3048;
  return /^ja(-|$)/i.test(culture ?? '') ? .91 : 1;
}

/** A definition's material as a wall's finish: its colour, the colour's alpha as the opacity (`#RRGGBBAA`), its type. */
export function materialSurface(material: CardDefinition['material']): Surface | undefined {
  const hex = material?.color?.trim().replace(/^#/, '') ?? '';
  const colour = /^[0-9a-f]{6}([0-9a-f]{2})?$/i.test(hex);
  if (!colour && !material?.type) return undefined;
  const surface: Surface = {};
  if (colour) {
    surface.color = [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16) / 255) as RGB;
    const alpha = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) : 255;
    if (alpha < 255) surface.opacity = alpha / 255;
  }
  if (material?.type) surface.type = material.type;
  return surface;
}

/**
 * The room a new wall stands in, as the app's view room answers for it: the height of its ceiling and the finish of its
 * first wall. Outside every room, the floor's height and its first wall's finish.
 */
export function roomAround(level: Pick<Level, 'rooms' | 'walls' | 'height'>, point: Vec2): { height: number; surface?: Surface } {
  const room = level.rooms.filter(r => r.polygon.length >= 3 && contains(r.polygon, point)).sort((a, b) => a.area - b.area)[0];
  const surface = (room ? level.walls.find(w => w.roomIndex === room.index) : level.walls[0])?.frontSurface;
  return { height: room?.height ?? level.height, ...(surface ? { surface } : {}) };
}

export interface CatalogWall { length: number; height: number; thickness: number; surface?: Surface }

/**
 * The wall a catalog interior wall, half wall, glass wall or column makes (FurnitureVariation.CreateFurniture): narrower
 * than 90 cm it keeps its width, wider it is one grid cell long; lower than 2 m it keeps its height, taller it reaches
 * the room's ceiling, a narrow one a centimetre short of it; its depth is its thickness. Its finish is its variation's
 * material (the glass), else the room's walls'.
 */
export function catalogWall(size: CatalogItem['size'], room: { height: number; surface?: Surface }, culture?: string, material?: Surface): CatalogWall {
  const [width, height, depth] = size, narrow = width < .9, surface = material ?? room.surface;
  return { length: narrow ? width : gridCellMeters(culture), height: height < 2 ? height : room.height - (narrow ? .01 : 0), thickness: depth,
    ...(surface ? { surface } : {}) };
}
